import React, { useState, useEffect, useCallback } from 'react';
import { sitesService, getErrorMessage } from '../services/api';
import api from '../services/api';
import { useAuth, ACCOUNT_TYPE_LABELS } from '../contexts/AuthContext';
import { turkeyProvinces, getDistricts } from '../data/turkeyLocations';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeInput,
  NativeBadge,
  NativeAlert,
  NativeModal,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  Plus, 
  Pencil, 
  Trash2,
  Search,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  Building2,
  UserCog,
  Send,
  CheckCircle,
  XCircle,
  MapPin,
  Check,
  FileText,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SitesPage() {
  const { user, isSuperAdmin, isAdminRole, isHesapYoneticisi, getEffectiveRole, getEffectiveSiteId } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Efektif rol kontrolü - simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const isEffectiveAdmin = ['super_admin', 'admin'].includes(effectiveRole);
  
  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    mode: 'create',
    siteId: null,
    saving: false
  });
  
  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    site: null,
    deleting: false
  });

  // Yönetici Atama Modal
  const [managerModal, setManagerModal] = useState({
    isOpen: false,
    site: null,
    manager: null,
    users: [],
    selectedUserId: null,
    loading: false,
    submitting: false
  });
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    province: '',
    district: '',
    neighborhood: '',
    street: '',
    building_no: '',
    apartment_no: '',
    latitude: '',
    longitude: '',
    is_open: true,
    max_stations: 10,
    max_power_kw: 100,
    account_type: 'apartman',
    // Faturalama bilgileri
    tax_id: '',      // Vergi No / TC
    phone: '',       // Telefon
    email: '',       // Email
    billing_address: '', // Fatura adresi (farklıysa)
    // Servis Ayarları
    primary_technician_id: '',
    backup_technician_ids: [],
    has_maintenance_contract: false,
    maintenance_contract_company: '',
    maintenance_contract_start: '',
    maintenance_contract_end: '',
    maintenance_sla_hours: '',
    maintenance_contract_pdf: ''
  });

  // Teknisyenler listesi (servis ayarları için)
  const [technicians, setTechnicians] = useState([]);

  // İlçe listesi (seçilen ile göre)
  const [districts, setDistricts] = useState([]);
  
  // Konum alma durumu
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationObtained, setLocationObtained] = useState(false);

  // İl değiştiğinde ilçeleri güncelle
  useEffect(() => {
    if (form.province) {
      const provinceData = turkeyProvinces.find(p => p.name === form.province);
      if (provinceData) {
        setDistricts(getDistricts(provinceData.code));
        // İl değiştiğinde ilçeyi sıfırla
        if (!getDistricts(provinceData.code).includes(form.district)) {
          setForm(prev => ({ ...prev, district: '' }));
        }
      }
    } else {
      setDistricts([]);
    }
  }, [form.province]);

  // Konum Al fonksiyonu
  const handleGetLocation = () => {
    // HTTPS kontrolü (iOS ve modern tarayıcılar için gerekli)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      toast.error('Konum özelliği sadece güvenli bağlantılarda (HTTPS) çalışır.');
      return;
    }
    
    if (!navigator.geolocation) {
      toast.error('Tarayıcınız konum özelliğini desteklemiyor');
      return;
    }
    
    setGettingLocation(true);
    setLocationObtained(false);
    
    // iOS Safari için izin durumunu kontrol et
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'denied') {
          setGettingLocation(false);
          toast.error(
            'Konum izni engellendi. iPhone\'da: Ayarlar > Safari > Konum Servisleri > İzin Ver seçeneğini aktif edin.',
            { duration: 6000 }
          );
          return;
        }
        // İzin granted veya prompt ise devam et
        requestGeolocation();
      }).catch(() => {
        // permissions API desteklenmiyorsa direkt dene
        requestGeolocation();
      });
    } else {
      // permissions API yoksa direkt dene (iOS Safari eski sürümler)
      requestGeolocation();
    }
  };
  
  const requestGeolocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm(prev => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        }));
        toast.success('Konum başarıyla alındı');
        setGettingLocation(false);
        setLocationObtained(true);
      },
      (error) => {
        setGettingLocation(false);
        console.error('Geolocation error:', error.code, error.message);
        
        switch (error.code) {
          case 1: // PERMISSION_DENIED
            toast.error(
              'Konum izni reddedildi. iPhone için: Ayarlar > Gizlilik > Konum Servisleri > Safari Siteleri > İzin Ver yapın.',
              { duration: 8000 }
            );
            break;
          case 2: // POSITION_UNAVAILABLE
            toast.error('Konum bilgisi alınamıyor. GPS sinyali zayıf olabilir.');
            break;
          case 3: // TIMEOUT
            toast.error('Konum alma zaman aşımına uğradı. Lütfen tekrar deneyin.');
            break;
          default:
            toast.error('Konum alınırken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,  // iOS için daha uzun timeout
        maximumAge: 0
      }
    );
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let data = await sitesService.list();
      
      // Hesap Yöneticisi ise sadece kendi sitesini göster (simülasyon desteği)
      if ((isEffectiveHesapYoneticisi || isHesapYoneticisi()) && effectiveSiteId) {
        data = (data || []).filter(site => site.id === effectiveSiteId);
      }
      
      setSites(data || []);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [isHesapYoneticisi, isEffectiveHesapYoneticisi, effectiveSiteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Teknisyenleri getir (servis ayarları için)
  const fetchTechnicians = useCallback(async () => {
    try {
      const token = localStorage.getItem('hims_token');
      const res = await fetch(`${API_URL}/api/technicians`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTechnicians(data.filter(t => t.is_active));
      }
    } catch (error) {
      console.error('Teknisyenler getirilemedi:', error);
    }
  }, []);

  useEffect(() => {
    fetchTechnicians();
  }, [fetchTechnicians]);

  const openCreateModal = () => {
    setForm({
      name: '',
      province: '',
      district: '',
      neighborhood: '',
      street: '',
      building_no: '',
      apartment_no: '',
      latitude: '',
      longitude: '',
      is_open: true,
      max_stations: 10,
      max_power_kw: 100,
      account_type: 'apartman',
      tax_id: '',
      phone: '',
      email: '',
      billing_address: '',
      // Servis Ayarları
      primary_technician_id: '',
      backup_technician_ids: [],
      has_maintenance_contract: false,
      maintenance_contract_company: '',
      maintenance_contract_start: '',
      maintenance_contract_end: '',
      maintenance_sla_hours: '',
      maintenance_contract_pdf: ''
    });
    setLocationObtained(false);
    setModal({
      isOpen: true,
      mode: 'create',
      siteId: null,
      saving: false
    });
  };

  const openEditModal = (site) => {
    // Mevcut adresi parse et (varsa)
    const addressParts = site.address ? site.address.split(', ') : [];
    let neighborhood = '', street = '', building_no = '', apartment_no = '';
    
    // Eski formattan parse etmeye çalış
    if (site.neighborhood) {
      neighborhood = site.neighborhood;
      street = site.street || '';
      building_no = site.building_no || '';
      apartment_no = site.apartment_no || '';
    } else if (addressParts.length > 0) {
      // Eski address_detail'den tahmin et
      neighborhood = addressParts[0] || '';
    }

    setForm({
      name: site.name || '',
      province: site.province || '',
      district: site.district || '',
      neighborhood: neighborhood,
      street: street,
      building_no: building_no,
      apartment_no: apartment_no,
      latitude: site.latitude || '',
      longitude: site.longitude || '',
      is_open: site.is_open ?? true,
      max_stations: site.max_stations || 10,
      max_power_kw: site.max_power_kw || 100,
      account_type: site.account_type || 'apartman',
      tax_id: site.tax_id || '',
      phone: site.phone || '',
      email: site.email || '',
      billing_address: site.billing_address || '',
      // Servis Ayarları
      primary_technician_id: site.primary_technician_id || '',
      backup_technician_ids: site.backup_technician_ids || [],
      has_maintenance_contract: site.has_maintenance_contract || false,
      maintenance_contract_company: site.maintenance_contract_company || '',
      maintenance_contract_start: site.maintenance_contract_start ? site.maintenance_contract_start.split('T')[0] : '',
      maintenance_contract_end: site.maintenance_contract_end ? site.maintenance_contract_end.split('T')[0] : '',
      maintenance_sla_hours: site.maintenance_contract_sla_hours || site.maintenance_sla_hours || '',
      maintenance_contract_pdf: site.maintenance_contract_pdf || ''
    });
    // Düzenleme modunda konum zaten varsa işaretle
    setLocationObtained(!!(site.latitude && site.longitude));
    setModal({
      isOpen: true,
      mode: 'edit',
      siteId: site.id,
      saving: false
    });
  };

  const closeModal = () => {
    if (modal.saving) return;
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  const openDeleteModal = (site) => {
    setDeleteModal({
      isOpen: true,
      site: site,
      deleting: false
    });
  };

  const closeDeleteModal = () => {
    if (deleteModal.deleting) return;
    setDeleteModal({ isOpen: false, site: null, deleting: false });
  };

  // Yönetici Modal Fonksiyonları
  const openManagerModal = async (site) => {
    setManagerModal({
      isOpen: true,
      site: site,
      manager: null,
      users: [],
      selectedUserId: null,
      loading: true,
      submitting: false
    });

    try {
      // Mevcut yöneticiyi ve kullanıcıları getir
      const [managerRes, usersRes] = await Promise.all([
        api.get(`/api/sites/${site.id}/manager`),
        api.get(`/api/sites/${site.id}/users`)
      ]);
      
      setManagerModal(prev => ({
        ...prev,
        manager: managerRes.data.manager,
        users: usersRes.data || [],
        loading: false
      }));
    } catch (err) {
      toast.error('Yönetici bilgileri yüklenemedi');
      setManagerModal(prev => ({ ...prev, loading: false }));
    }
  };

  const closeManagerModal = () => {
    if (managerModal.submitting) return;
    setManagerModal({
      isOpen: false,
      site: null,
      manager: null,
      users: [],
      selectedUserId: null,
      loading: false,
      submitting: false
    });
  };

  const handleManagerAssignment = async () => {
    if (!managerModal.selectedUserId || managerModal.submitting) return;

    setManagerModal(prev => ({ ...prev, submitting: true }));

    try {
      await api.post('/api/manager-assignments', {
        site_id: managerModal.site.id,
        user_id: managerModal.selectedUserId
      });
      
      toast.success('Yönetici atama talebi Süper Admin onayına gönderildi');
      closeManagerModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Talep gönderilemedi');
      setManagerModal(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (modal.saving) return;
    
    // İl ve İlçe zorunlu alan kontrolü
    if (!form.province || !form.district) {
      toast.error('İl ve İlçe alanları zorunludur (Usta sıralama için gerekli)');
      return;
    }
    
    // Zorunlu alan kontrolü
    if (!form.tax_id || !form.phone || !form.email) {
      toast.error('Vergi No/TC, Telefon ve Email alanları zorunludur');
      return;
    }
    
    setModal(prev => ({ ...prev, saving: true }));
    
    try {
      // Adresi Türkiye formatında birleştir: Mahalle, Cadde/Sokak, Bina No, Daire No, İlçe, İl
      const addressParts = [];
      if (form.neighborhood) addressParts.push(form.neighborhood);
      if (form.street) addressParts.push(form.street);
      if (form.building_no) addressParts.push(`No: ${form.building_no}`);
      if (form.apartment_no) addressParts.push(`Daire: ${form.apartment_no}`);
      if (form.district) addressParts.push(form.district);
      if (form.province) addressParts.push(form.province);
      const fullAddress = addressParts.join(', ');

      const payload = {
        name: form.name,
        address: fullAddress,
        province: form.province,
        district: form.district,
        neighborhood: form.neighborhood,
        street: form.street,
        building_no: form.building_no,
        apartment_no: form.apartment_no,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        is_open: form.is_open,
        max_stations: parseInt(form.max_stations) || 10,
        max_power_kw: parseFloat(form.max_power_kw) || 100,
        account_type: form.account_type,
        // Faturalama bilgileri
        tax_id: form.tax_id,
        phone: form.phone,
        email: form.email,
        billing_address: form.billing_address || fullAddress,
        // Servis Ayarları
        primary_technician_id: form.primary_technician_id || null,
        backup_technician_ids: form.backup_technician_ids || [],
        has_maintenance_contract: form.has_maintenance_contract,
        maintenance_contract_company: form.maintenance_contract_company || null,
        maintenance_contract_start: form.maintenance_contract_start || null,
        maintenance_contract_end: form.maintenance_contract_end || null,
        maintenance_contract_sla_hours: form.maintenance_sla_hours ? parseInt(form.maintenance_sla_hours) : null,
        maintenance_contract_pdf: form.maintenance_contract_pdf || null
      };

      if (modal.mode === 'create') {
        await sitesService.create(payload);
        toast.success('Hesap başarıyla oluşturuldu');
      } else {
        await sitesService.update(modal.siteId, payload);
        toast.success('Hesap basariyla guncellendi');
      }
      
      setModal({ isOpen: false, mode: 'create', siteId: null, saving: false });
      fetchData();
      
    } catch (err) {
      toast.error(getErrorMessage(err));
      setModal(prev => ({ ...prev, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.site || deleteModal.deleting) return;
    
    setDeleteModal(prev => ({ ...prev, deleting: true }));
    
    try {
      await sitesService.delete(deleteModal.site.id);
      toast.success('Site basariyla silindi');
      setDeleteModal({ isOpen: false, site: null, deleting: false });
      fetchData();
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      if (errorMsg.includes('istasyon') || errorMsg.includes('station')) {
        toast.error(errorMsg, { duration: 5000 });
      } else {
        toast.error(errorMsg);
      }
      setDeleteModal(prev => ({ ...prev, deleting: false }));
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const filteredSites = sites.filter(site => 
    site.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    site.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="sites-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Hesaplar</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Şarj istasyonu hesaplarını yönetin</p>
        </div>
              {(isSuperAdmin() || isAdminRole()) && (
          <NativeButton onClick={openCreateModal} data-testid="create-site-button">
            <Plus style={{ width: '16px', height: '16px' }} />
            Yeni Hesap
          </NativeButton>
        )}
      </div>

      {error && (
        <NativeAlert variant="danger" icon={AlertCircle}>
          {error}
        </NativeAlert>
      )}

      {/* Search */}
      <NativeCard>
        <NativeCardContent>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
              <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px' }} className="text-slate-400" />
              <input
                type="text"
                placeholder="Hesap ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 40px',
                  borderRadius: '8px',
                  border: '1px solid var(--card-border)',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--card-bg)'
                }}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
            <NativeButton variant="outline" onClick={fetchData}>
              <RefreshCw style={{ width: '16px', height: '16px' }} />
              Yenile
            </NativeButton>
          </div>
        </NativeCardContent>
      </NativeCard>

      {/* Sites Table */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Building2}>
            Hesap Listesi ({filteredSites.length})
          </NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          <NativeTable>
            <NativeTableHeader>
              <NativeTableHead>Hesap Adı</NativeTableHead>
              <NativeTableHead>Hesap Tipi</NativeTableHead>
              <NativeTableHead>Adres</NativeTableHead>
              <NativeTableHead>Max İstasyon</NativeTableHead>
              <NativeTableHead>Güç Kapasitesi</NativeTableHead>
              <NativeTableHead align="right">İşlemler</NativeTableHead>
            </NativeTableHeader>
            <NativeTableBody>
              {filteredSites.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={Building2}
                      title="Hesap bulunamadı"
                      description="Henüz hesap eklenmemiş veya arama kriterlerinize uygun hesap yok"
                      action={
                        <NativeButton onClick={openCreateModal}>
                          <Plus style={{ width: '16px', height: '16px' }} />
                          İlk Hesabı Oluştur
                        </NativeButton>
                      }
                    />
                  </td>
                </tr>
              ) : (
                filteredSites.map((site) => (
                  <NativeTableRow key={site.id}>
                    <NativeTableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: '500' }}>{site.name}</span>
                        {/* İl/İlçe eksik uyarısı */}
                        {(!site.province || !site.district) && (
                          <span 
                            style={{ 
                              padding: '2px 6px', 
                              borderRadius: '4px', 
                              backgroundColor: '#fef3c7', 
                              color: '#d97706',
                              fontSize: '10px',
                              fontWeight: '500'
                            }}
                            title="İl/İlçe bilgisi eksik - Usta sıralama için gerekli"
                          >
                            ⚠️ İl/İlçe Eksik
                          </span>
                        )}
                      </div>
                    </NativeTableCell>
                    <NativeTableCell>
                      <NativeBadge variant="default">
                        {ACCOUNT_TYPE_LABELS[site.account_type] || 'Apartman'}
                      </NativeBadge>
                    </NativeTableCell>
                    <NativeTableCell>{site.address || '-'}</NativeTableCell>
                    <NativeTableCell>{site.max_stations || 10}</NativeTableCell>
                    <NativeTableCell>
                      <span style={{ color: '#2563eb', fontWeight: '500' }}>
                        {site.max_power_kw || 100} kW
                      </span>
                    </NativeTableCell>
                    <NativeTableCell align="right">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        {/* Yönetici Atama Butonu */}
                        <NativeButton
                          variant="ghost"
                          size="icon"
                          onClick={() => openManagerModal(site)}
                          title="Yönetici"
                          data-testid={`manager-site-${site.id}`}
                        >
                          <UserCog style={{ width: '16px', height: '16px', color: '#2563eb' }} />
                        </NativeButton>
                        <NativeButton
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(site)}
                          data-testid={`edit-site-${site.id}`}
                        >
                          <Pencil style={{ width: '16px', height: '16px' }} />
                        </NativeButton>
                        {/* Site Manager site silemez */}
                        {isSuperAdmin() && (
                          <NativeButton
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteModal(site)}
                            style={{ color: '#dc2626' }}
                            data-testid={`delete-site-${site.id}`}
                          >
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                          </NativeButton>
                        )}
                      </div>
                    </NativeTableCell>
                  </NativeTableRow>
                ))
              )}
            </NativeTableBody>
          </NativeTable>
        </NativeCardContent>
      </NativeCard>

      {/* Create/Edit Modal */}
      <NativeModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.mode === 'create' ? 'Yeni Hesap Oluştur' : 'Hesap Düzenle'}
        disabled={modal.saving}
      >
        <form onSubmit={handleSubmit}>
          <NativeInput
            label="Hesap Adı"
            id="name"
            value={form.name}
            onChange={(e) => updateForm('name', e.target.value)}
            placeholder="Yeşil Vadi Sitesi"
            required
          />

          <div style={{ marginBottom: '16px' }}>
            <label className="text-slate-700 dark:text-slate-200" style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
              Hesap Tipi
            </label>
            <select
              id="account_type"
              value={form.account_type}
              onChange={(e) => updateForm('account_type', e.target.value)}
              style={{
                width: '100%',
                height: '40px',
                padding: '0 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: 'white',
                fontSize: '14px'
              }}
              className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            >
              <option value="apartman">Apartman/Site</option>
              <option value="fabrika">Fabrika</option>
              <option value="avm">AVM</option>
              <option value="lojistik">Lojistik/Filo</option>
              <option value="belediye">Belediye/Kamu</option>
              <option value="bireysel">Bireysel</option>
            </select>
          </div>

          {/* İl Seçimi - Her zaman görünür */}
          <div style={{ marginBottom: '16px' }}>
            <label className="text-slate-700 dark:text-slate-200" style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
              İl <span style={{ color: '#dc2626' }}>* (Zorunlu)</span>
            </label>
            <select
              id="province"
              value={form.province}
              onChange={(e) => updateForm('province', e.target.value)}
              required
              style={{
                width: '100%',
                height: '40px',
                padding: '0 12px',
                border: form.province ? '1px solid #d1d5db' : '2px solid #dc2626',
                borderRadius: '8px',
                backgroundColor: 'white',
                fontSize: '14px'
              }}
              className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
            >
              <option value="">İl Seçin</option>
              {turkeyProvinces.map(p => (
                <option key={p.code} value={p.name}>{p.name}</option>
              ))}
            </select>
            {!form.province && (
              <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>Usta sıralama için il bilgisi gereklidir</p>
            )}
          </div>

          {/* İlçe Seçimi - İl seçilince görünür */}
          {form.province && (
            <div style={{ marginBottom: '16px' }}>
              <label className="text-slate-700 dark:text-slate-200" style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }}>
                İlçe <span style={{ color: '#dc2626' }}>* (Zorunlu)</span>
              </label>
              <select
                id="district"
                value={form.district}
                onChange={(e) => updateForm('district', e.target.value)}
                required
                style={{
                  width: '100%',
                  height: '40px',
                  padding: '0 12px',
                  border: form.district ? '1px solid #d1d5db' : '2px solid #dc2626',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  fontSize: '14px'
                }}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              >
                <option value="">İlçe Seçin</option>
                {districts.map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}

          {/* Mahalle/Köy - İlçe seçilince görünür */}
          {form.district && (
            <NativeInput
              label="Mahalle/Köy *"
              id="neighborhood"
              value={form.neighborhood}
              onChange={(e) => updateForm('neighborhood', e.target.value)}
              placeholder="Örn: Atatürk Mahallesi"
              required
            />
          )}

          {/* Cadde/Sokak, Bina No, Daire No - İlçe seçilince hep birlikte görünür */}
          {form.district && (
            <>
              <NativeInput
                label="Cadde/Sokak"
                id="street"
                value={form.street}
                onChange={(e) => updateForm('street', e.target.value)}
                placeholder="Örn: İstiklal Caddesi"
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <NativeInput
                  label="Bina No"
                  id="building_no"
                  value={form.building_no}
                  onChange={(e) => updateForm('building_no', e.target.value)}
                  placeholder="Örn: 15"
                />
                <NativeInput
                  label="Daire No"
                  id="apartment_no"
                  value={form.apartment_no}
                  onChange={(e) => updateForm('apartment_no', e.target.value)}
                  placeholder="Örn: 3"
                />
              </div>
            </>
          )}

          {/* Konum Al Butonu - Her zaman görünür */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <NativeButton
                type="button"
                variant={locationObtained ? 'success' : 'outline'}
                size="sm"
                onClick={handleGetLocation}
                disabled={gettingLocation}
                data-testid="get-location-btn"
                style={locationObtained ? { backgroundColor: '#22c55e', color: 'white' } : {}}
              >
                {gettingLocation ? (
                  <>
                    <Loader2 className="animate-spin" style={{ width: '14px', height: '14px' }} />
                    Konum Alınıyor...
                  </>
                ) : locationObtained ? (
                  <>
                    <Check style={{ width: '14px', height: '14px' }} />
                    Konum Alındı
                  </>
                ) : (
                  <>
                    <MapPin style={{ width: '14px', height: '14px' }} />
                    Konumu Al
                  </>
                )}
              </NativeButton>
              {locationObtained && (
                <span style={{ color: '#22c55e', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  ✅ Konum kaydedildi
                </span>
              )}
            </div>
          </div>

          <NativeInput
            label="Maksimum İstasyon Sayısı"
            id="max_stations"
            type="number"
            min="1"
            value={form.max_stations}
            onChange={(e) => updateForm('max_stations', e.target.value)}
          />

          <NativeInput
            label="Maksimum Guc Kapasitesi (kW)"
            id="max_power_kw"
            type="number"
            min="1"
            step="0.1"
            value={form.max_power_kw}
            onChange={(e) => updateForm('max_power_kw', e.target.value)}
            data-testid="max-power-input"
          />
          <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '12px', marginTop: '-8px', marginBottom: '12px' }}>
            Yuk dengeleme icin sitenin toplam guc kapasitesi
          </p>

          {/* Faturalama Bilgileri Bölümü */}
          <div style={{ 
            backgroundColor: '#f0f9ff', 
            border: '1px solid #bae6fd', 
            borderRadius: '12px', 
            padding: '16px', 
            marginTop: '16px',
            marginBottom: '16px'
          }} className="dark:bg-slate-800 dark:border-slate-700">
            <h4 className="text-blue-700 dark:text-blue-400" style={{ fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileText style={{ width: '18px', height: '18px' }} />
              Faturalama Bilgileri
              <span style={{ fontSize: '11px', fontWeight: '400', backgroundColor: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: '9999px' }}>Zorunlu</span>
            </h4>

            <NativeInput
              label="Vergi No / TC Kimlik No *"
              id="tax_id"
              value={form.tax_id}
              onChange={(e) => updateForm('tax_id', e.target.value)}
              placeholder="12345678901"
              required
              data-testid="tax-id-input"
            />
            <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '12px', marginTop: '-8px', marginBottom: '12px' }}>
              Kurumsal fatura için vergi numarası veya TC kimlik numarası
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <NativeInput
                  label="Telefon *"
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => updateForm('phone', e.target.value)}
                  placeholder="0532 123 45 67"
                  required
                  data-testid="phone-input"
                />
                <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '12px', marginTop: '-8px', marginBottom: '0' }}>
                  Acil iletişim ve bildirim
                </p>
              </div>
              <div>
                <NativeInput
                  label="Email *"
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateForm('email', e.target.value)}
                  placeholder="fatura@sirket.com"
                  required
                  data-testid="email-input"
                />
                <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '12px', marginTop: '-8px', marginBottom: '0' }}>
                  Fatura ve bildirim
                </p>
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <NativeInput
                label="Fatura Adresi (Farklıysa)"
                id="billing_address"
                value={form.billing_address}
                onChange={(e) => updateForm('billing_address', e.target.value)}
                placeholder="Fatura adresi site adresinden farklıysa doldurun"
                data-testid="billing-address-input"
              />
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '12px', marginTop: '-8px', marginBottom: '0' }}>
                Boş bırakılırsa site adresi kullanılır
              </p>
            </div>
          </div>

          {/* Servis Ayarları Bölümü */}
          <div style={{ 
            backgroundColor: '#fef3c7', 
            borderRadius: '12px', 
            padding: '20px',
            marginBottom: '16px'
          }} className="dark:bg-amber-900/30">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <Wrench className="h-5 w-5 text-amber-600" />
              <h3 className="text-amber-800 dark:text-amber-300" style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                Servis Ayarları
              </h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              {/* Öncelikli Usta */}
              <div>
                <label className="text-slate-700 dark:text-slate-300" style={{ fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                  Öncelikli Usta
                </label>
                <select
                  value={form.primary_technician_id}
                  onChange={(e) => updateForm('primary_technician_id', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: 'white',
                    fontSize: '14px'
                  }}
                  className="dark:bg-slate-800 dark:border-slate-600 dark:text-white"
                  data-testid="primary-technician-select"
                >
                  <option value="">Seçin...</option>
                  {technicians.map(tech => (
                    <option key={tech.id} value={tech.id}>
                      {tech.full_name || tech.name} ({tech.technician_type === 'hims_team' ? 'HIMS' : tech.technician_type === 'contracted' ? 'Anlaşmalı' : 'Taşeron'})
                    </option>
                  ))}
                </select>
                <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '11px', marginTop: '4px' }}>
                  Bu site için varsayılan usta
                </p>
              </div>

              {/* Bakım Sözleşmesi */}
              <div>
                <label className="text-slate-700 dark:text-slate-300" style={{ fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '6px' }}>
                  Bakım Sözleşmesi
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb' }} className="dark:bg-slate-800 dark:border-slate-600">
                  <input
                    type="checkbox"
                    checked={form.has_maintenance_contract}
                    onChange={(e) => updateForm('has_maintenance_contract', e.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: '#f59e0b' }}
                    data-testid="maintenance-contract-checkbox"
                  />
                  <span className="text-slate-700 dark:text-slate-300" style={{ fontSize: '14px' }}>
                    Bakım sözleşmesi var
                  </span>
                </div>
              </div>
            </div>

            {/* Bakım Sözleşmesi Detayları - Sadece checkbox işaretliyse göster */}
            {form.has_maintenance_contract && (
              <div style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                padding: '16px',
                marginTop: '12px',
                border: '1px solid #fcd34d'
              }} className="dark:bg-slate-800 dark:border-amber-600/50">
                {/* Sözleşme Bitiş Uyarısı */}
                {form.maintenance_contract_end && (() => {
                  const endDate = new Date(form.maintenance_contract_end);
                  const today = new Date();
                  const diffDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
                  
                  if (diffDays < 0) {
                    return (
                      <div style={{ 
                        padding: '10px 14px', 
                        backgroundColor: '#fee2e2', 
                        border: '1px solid #fecaca',
                        borderRadius: '8px', 
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }} className="dark:bg-red-900/30 dark:border-red-800">
                        <span style={{ fontSize: '18px' }}>⚠️</span>
                        <span style={{ color: '#dc2626', fontWeight: '500', fontSize: '14px' }} className="dark:text-red-400">
                          Sözleşme süresi {Math.abs(diffDays)} gün önce doldu!
                        </span>
                      </div>
                    );
                  } else if (diffDays <= 30) {
                    return (
                      <div style={{ 
                        padding: '10px 14px', 
                        backgroundColor: '#fef3c7', 
                        border: '1px solid #fde68a',
                        borderRadius: '8px', 
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }} className="dark:bg-yellow-900/30 dark:border-yellow-800">
                        <span style={{ fontSize: '18px' }}>⏰</span>
                        <span style={{ color: '#d97706', fontWeight: '500', fontSize: '14px' }} className="dark:text-yellow-400">
                          Sözleşme bitimine {diffDays} gün kaldı!
                        </span>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <NativeInput
                    label="Sözleşmeli Firma"
                    value={form.maintenance_contract_company}
                    onChange={(e) => updateForm('maintenance_contract_company', e.target.value)}
                    placeholder="Örn: ABC Servis Ltd."
                    data-testid="contract-company-input"
                  />
                  <NativeInput
                    label="SLA Müdahale Süresi (Saat)"
                    type="number"
                    value={form.maintenance_sla_hours}
                    onChange={(e) => updateForm('maintenance_sla_hours', e.target.value)}
                    placeholder="Örn: 4"
                    data-testid="sla-hours-input"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <NativeInput
                    label="Sözleşme Başlangıç"
                    type="date"
                    value={form.maintenance_contract_start}
                    onChange={(e) => updateForm('maintenance_contract_start', e.target.value)}
                    data-testid="contract-start-input"
                  />
                  <NativeInput
                    label="Sözleşme Bitiş"
                    type="date"
                    value={form.maintenance_contract_end}
                    onChange={(e) => updateForm('maintenance_contract_end', e.target.value)}
                    data-testid="contract-end-input"
                  />
                </div>
                
                {/* PDF Yükleme Bölümü */}
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#f8fafc', 
                  borderRadius: '8px',
                  border: '1px dashed #cbd5e1'
                }} className="dark:bg-slate-700 dark:border-slate-600">
                  <label className="text-slate-700 dark:text-slate-300" style={{ fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                    Sözleşme Belgesi (PDF)
                  </label>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // 10MB limit
                            const maxSize = 10 * 1024 * 1024;
                            if (file.size > maxSize) {
                              toast.error(`Dosya çok büyük (${(file.size / 1024 / 1024).toFixed(1)}MB). Maksimum 10MB yüklenebilir.`);
                              e.target.value = '';
                              return;
                            }
                            
                            // Progress state
                            updateForm('_uploadProgress', 0);
                            
                            try {
                              // Upload file to server
                              const token = localStorage.getItem('hims_token');
                              const formDataUpload = new FormData();
                              formDataUpload.append('file', file);
                              
                              updateForm('_uploadProgress', 30);
                              
                              const response = await fetch(`${API_URL}/api/upload`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` },
                                body: formDataUpload
                              });
                              
                              updateForm('_uploadProgress', 80);
                              
                              if (response.ok) {
                                const data = await response.json();
                                updateForm('maintenance_contract_pdf', data.url);
                                updateForm('_uploadProgress', 100);
                                setTimeout(() => updateForm('_uploadProgress', null), 1000);
                                toast.success('PDF yüklendi');
                              } else {
                                const error = await response.json();
                                toast.error(error.detail || 'Dosya yüklenirken hata oluştu');
                                updateForm('_uploadProgress', null);
                              }
                            } catch (error) {
                              toast.error('Dosya yüklenirken hata oluştu');
                              updateForm('_uploadProgress', null);
                            }
                          }
                        }}
                        style={{ 
                          fontSize: '14px',
                          padding: '8px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          backgroundColor: 'white',
                          flex: 1
                        }}
                        className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300"
                        data-testid="contract-pdf-input"
                      />
                      
                      {form.maintenance_contract_pdf && (
                        <button
                          type="button"
                          onClick={() => {
                            // Handle both URL paths and legacy base64
                            const pdfUrl = form.maintenance_contract_pdf.startsWith('/uploads/') 
                              ? `${API_URL}${form.maintenance_contract_pdf}`
                              : form.maintenance_contract_pdf;
                            const link = document.createElement('a');
                            link.href = pdfUrl;
                            link.download = `sozlesme_${form.name || 'belge'}.pdf`;
                            link.target = '_blank';
                            link.click();
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          data-testid="download-contract-btn"
                        >
                          📄 Belgeyi İndir
                        </button>
                      )}
                    </div>
                    
                    {/* Upload Progress Bar */}
                    {form._uploadProgress !== null && form._uploadProgress !== undefined && (
                      <div style={{ width: '100%', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                        <div 
                          style={{ 
                            width: `${form._uploadProgress}%`, 
                            height: '6px', 
                            backgroundColor: form._uploadProgress === 100 ? '#10b981' : '#3b82f6',
                            transition: 'width 0.3s ease'
                          }} 
                        />
                        <span style={{ fontSize: '12px', color: '#64748b', display: 'block', textAlign: 'center', marginTop: '4px' }}>
                          {form._uploadProgress === 100 ? 'Tamamlandı!' : `Yükleniyor... ${form._uploadProgress}%`}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {form.maintenance_contract_pdf && (
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#10b981', fontSize: '13px' }}>✓ PDF yüklendi</span>
                      <button
                        type="button"
                        onClick={() => updateForm('maintenance_contract_pdf', '')}
                        style={{ 
                          color: '#ef4444', 
                          fontSize: '13px', 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        Kaldır
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '8px' }}>
            <NativeButton type="button" variant="outline" onClick={closeModal} disabled={modal.saving}>
              Iptal
            </NativeButton>
            <NativeButton type="submit" disabled={modal.saving}>
              {modal.saving ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Kaydediliyor...
                </>
              ) : (
                modal.mode === 'create' ? 'Oluştur' : 'Kaydet'
              )}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Delete Confirmation Modal */}
      <NativeModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        title="Hesap Silme Onayı"
        disabled={deleteModal.deleting}
      >
        <div>
          <NativeAlert variant="warning" icon={AlertTriangle}>
            <p style={{ fontWeight: '500', marginBottom: '4px' }}>Dikkat!</p>
            <p style={{ margin: 0, fontSize: '14px' }}>Bu hesaba bağlı istasyonlar varsa silme işlemi başarısız olacaktır. Önce istasyonları kaldırmanız gerekir.</p>
          </NativeAlert>
          
          {deleteModal.site && (
            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', marginTop: '16px' }} className="dark:bg-slate-800">
              <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', marginBottom: '8px' }}>Silinecek hesap:</p>
              <p className="text-slate-900 dark:text-white" style={{ fontWeight: '500', margin: 0 }}>{deleteModal.site.name}</p>
              {deleteModal.site.address && (
                <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', marginTop: '4px' }}>{deleteModal.site.address}</p>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={closeDeleteModal} disabled={deleteModal.deleting}>
              İptal
            </NativeButton>
            <NativeButton 
              type="button" 
              variant="danger"
              onClick={handleDelete}
              disabled={deleteModal.deleting}
              data-testid="confirm-delete-site"
            >
              {deleteModal.deleting ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Siliniyor...
                </>
              ) : (
                'Sil'
              )}
            </NativeButton>
          </div>
        </div>
      </NativeModal>

      {/* Yönetici Atama Modal */}
      <NativeModal
        isOpen={managerModal.isOpen}
        onClose={closeManagerModal}
        title={`Yönetici - ${managerModal.site?.name || ''}`}
        disabled={managerModal.submitting}
      >
        <div>
          {managerModal.loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
              <Loader2 className="animate-spin" style={{ width: '32px', height: '32px', color: '#3b82f6' }} />
            </div>
          ) : (
            <>
              {/* Mevcut Yönetici */}
              <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '12px' }} className="dark:bg-slate-800">
                <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Mevcut Yönetici
                </p>
                {managerModal.manager ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600' }}>
                      {managerModal.manager.full_name?.charAt(0) || managerModal.manager.email?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="text-slate-900 dark:text-white" style={{ fontWeight: '500', margin: 0 }}>
                        {managerModal.manager.full_name || managerModal.manager.email}
                      </p>
                      <p className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px', margin: 0 }}>
                        {managerModal.manager.email}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-400 dark:text-slate-500" style={{ fontStyle: 'italic', margin: 0 }}>
                    Henüz yönetici atanmamış
                  </p>
                )}
              </div>

              {/* Yönetici Ata */}
              <div>
                <p className="text-slate-700 dark:text-slate-200" style={{ fontWeight: '500', marginBottom: '12px' }}>
                  Yeni Yönetici Seç
                </p>
                
                {managerModal.users.length === 0 ? (
                  <NativeAlert variant="info">
                    <p style={{ margin: 0 }}>Bu hesapta henüz kullanıcı yok. Önce kullanıcı eklemeniz gerekiyor.</p>
                  </NativeAlert>
                ) : (
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }} className="dark:border-slate-700">
                    {managerModal.users.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => setManagerModal(prev => ({ ...prev, selectedUserId: user.id }))}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #e5e7eb',
                          backgroundColor: managerModal.selectedUserId === user.id ? '#eff6ff' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                        className={`dark:border-slate-700 ${managerModal.selectedUserId === user.id ? 'dark:bg-blue-900/30' : 'dark:hover:bg-slate-700/50'}`}
                      >
                        <div style={{ 
                          width: '32px', height: '32px', borderRadius: '50%', 
                          backgroundColor: managerModal.selectedUserId === user.id ? '#3b82f6' : '#94a3b8',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', 
                          color: 'white', fontSize: '14px', fontWeight: '500'
                        }}>
                          {user.full_name?.charAt(0) || user.email?.charAt(0) || '?'}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p className="text-slate-900 dark:text-white" style={{ margin: 0, fontWeight: '500' }}>
                            {user.full_name || user.email}
                          </p>
                          <p className="text-slate-500 dark:text-slate-400" style={{ margin: 0, fontSize: '12px' }}>
                            {user.email}
                          </p>
                        </div>
                        {managerModal.selectedUserId === user.id && (
                          <CheckCircle style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Butonlar */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }} className="dark:border-slate-700">
                <NativeButton variant="outline" onClick={closeManagerModal} disabled={managerModal.submitting}>
                  İptal
                </NativeButton>
                <NativeButton 
                  onClick={handleManagerAssignment}
                  disabled={!managerModal.selectedUserId || managerModal.submitting}
                >
                  {managerModal.submitting ? (
                    <>
                      <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send style={{ width: '16px', height: '16px' }} />
                      Onaya Gönder
                    </>
                  )}
                </NativeButton>
              </div>
            </>
          )}
        </div>
      </NativeModal>
    </div>
  );
}
